import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Upload, Camera, IdCard, Trash, Image, CircleX } from 'lucide-react';
import { useNavigate } from "react-router-dom";
import { PutObjectCommand } from "@aws-sdk/client-s3";
import { ModalRemoveDocReact } from '../../modals/ModalRemoveDocReact.js';

import { createS3Client } from "../../credentials/awsClients.js";

import { buildCnhPdf } from '../../utils/BuildCnhPdf.js';
import { ModalErroReact } from '../../modals/ModalErroReact.js';

import './upload-documento.scss';
import HeaderHora from '../../components/headerHora.tsx';
import { CountdownBar } from '../../components/CountdownBar.tsx';
import InternetStatus from '../../components/conexao.tsx';

const s3Client = createS3Client();

export function UploadDocumentReact() {
    const documentDetector = useRef(null);
    const [imagesUploaded, setImagesUploaded] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMsg, setLoadingMsg] = useState({title: 'Preparando para capturar o documento.', subTitle: 'Estamos ajustando o ambiente para a captura. Isso leva apenas alguns instantes.'});
    const [isOpen, setIsOpen] = useState(false);
    const [toDeleteIndex, setToDeleteIndex] = useState(null);
    const [modalErroOpen, setModalErroOpen] = useState(false);
    const [mensagemErro, setMensagemErro] = useState(false);

    const navigate = useNavigate();

    const convertDoc = useCallback(async () => {
        if(imagesUploaded.length > 1 || imagesUploaded[0].side === 'both'){
            try {
                const result = await buildCnhPdf(imagesUploaded, '');
                setImagesUploaded([result])
            } catch (e) {
                setMensagemErro({title: "Falha ao gerar PDF",text: String(e)});
                setModalErroOpen(true);
            }
        }
    }, [imagesUploaded]);
    
    const waitForCAF = () => new Promise((resolve, reject) => {
      const maxWaitMs = 8000;
      const started = Date.now();

      const tick = () => {
        const mod = window?.["@combateafraude/document-detector"];
        if (mod?.DocumentDetector) return resolve(mod);
        if (Date.now() - started > maxWaitMs)
          return reject(new Error("CAF DocumentDetector não carregou."));
        requestAnimationFrame(tick);
      };
      tick();
    });

    useEffect(() => {
        let cancelled = false;
        let infoLink = JSON.parse(sessionStorage.getItem('dadosLinkAtual'));

        if(infoLink.outArquivo === 'S'){
            navigate(infoLink.outServico === 'KYC' ? '/comprovante-residencia' : (infoLink.outServico === 'IDD' ? '/documentos-idd' : '/face'));
        }

        (async () => {
            var sessionToken;
            
            try{
                const res = await fetch(`${process.env.REACT_APP_URL_API}/session/sdk`, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${sessionStorage.getItem('sessionTokenBearer')}`,
                        'x-Token': sessionStorage.getItem('sessionToken'),
                    }
                });
                
                const data = await res.json();

                if(data.erros === false){
                    sessionToken = data.retorno;
                }
                else{
                    setMensagemErro({title: "Erro ao gerar sessão para OCR",text: data.retorno ? data.retorno : String(data)});
                    setModalErroOpen(true);
                }
            }
            catch (err){
                setMensagemErro({title: "Erro na tentativa de gerar a sessão",text: String(err)});
                setModalErroOpen(true);
            }
        
            try {
                const mod = await waitForCAF();
                if (cancelled) return;

                const { DocumentDetector } = mod;

                const options = {
                    token: sessionToken,
                    language: "pt_BR",
                    blockExecutionOnDesktops: false,
                    enableVisibilityChangeSecurity: false,
                    enableFramingAnalyzer: true,
                    analytics: {
                        enabled: false,
                        enableDebugMode: false,
                    },
                    appearance: {
                        general: {
                        fontFamily: "Roboto",
                        closeButtonIconColor: "#FFFFFF",
                        },
                        capture: {
                        captureButtonIconSize: "100%",
                        captureButtonColor: "#FFFFFF",
                        hideCaptureTitle: false,
                        },
                        upload: {
                        backgroundColor: "#BDBDBD",
                        card: {
                            backgroundColor: "#FFFFFF",
                        },
                        startScreen: {
                            title: { color: "#323232" },
                            details: { color: "#828282" },
                            allowButton: {
                            backgroundColor: "var(--brand-color)",
                            label: { color: "#FFFFFF" }
                            }
                        },
                        loadingScreen: {
                            icon: { color: "#000000" },
                            text: { color: "#323232" }
                        },
                        failureScreen: {
                            icon: { color: "#E21B45", shadowColor: "#FFE4E6" },
                            title: { color: "#323232" },
                            details: { color: "#828282" },
                            retryButton: {
                            backgroundColor: "#E21B45",
                            label: { color: "#FFFFFF" }
                            }
                        },
                        successScreen: {
                            icon: { color: "#0BAA43", shadowColor: "#DAFEE5" },
                            text: { color: "#0BAA43" }
                        }
                        }
                    },
                };
                documentDetector.current = new DocumentDetector(options);
                await documentDetector.current.loadAiModel();
                await documentDetector.current.initialize();

                setLoading(false)
            }
            catch (err) {
                setMensagemErro({title: "Erro ao inicializar a ferramenta de OCR",text: String(err)});
                setModalErroOpen(true);
            }
        })();
    }, [navigate]);

    useEffect(() => {
        let isMounted = true;

        const img0 = imagesUploaded?.[0];
        if (!img0) return;

        const isCNH = ['cnh_new', 'cnh', 'cnh_full'].includes(img0.documento);
        sessionStorage.setItem('tipoDoc', isCNH ? 'habilitado' : 'naoHabilitado');

        (async () => {
            if (img0.side !== 'both' || img0.tipo !== 'document') {
                convertDoc?.();
                return;
            }

            if (!img0.blob) return;

            const MAX_SIZE = 3 * 1024 * 1024;

            if (img0.blob.size > MAX_SIZE) {
                if (isMounted) {
                    setLoading(false);
                    setMensagemErro({title: "Arquivo muito grande!", text: 'O arquivo gerado ultrapassa o tamanho máximo permitido de 3MB. Por favor, tente novamente com fotos de melhor qualidade ou menos detalhes.'});
                    setModalErroOpen(true);
                }
                return;
            }

            if (isMounted){
                setLoading(true);
                setLoadingMsg({title: 'Transferindo seu documento com segurança.', subTitle: 'Transferindo seu documento com segurança. Isso pode levar alguns segundos, dependendo da sua conexão. Por favor, não feche a tela.'});
            } 

            try {
                const extFromName = img0.blob.name?.split('.').pop()?.toLowerCase();
                const extFromMime = img0.blob.type?.split('/').pop()?.toLowerCase();
                const ext = extFromName || extFromMime || 'bin';

                const fileName = `${crypto.randomUUID()}.${ext}`;
                const key = `${JSON.parse(sessionStorage.getItem('dadosLinkAtual')).outDocumento}/docs/${fileName}`;

                const putParams = {
                    Bucket: process.env.REACT_APP_AWS_BUCKET,
                    Key: key,
                    Body: img0.blob,
                    ContentType: img0.blob.type || 'application/octet-stream',
                };

                const putFile = await s3Client.send(new PutObjectCommand(putParams));

                if (putFile?.$metadata?.httpStatusCode === 200) {
                    try {
                        const res = await fetch(`${process.env.REACT_APP_URL_API}/conform/send-file`, {
                            method: 'POST',
                            headers: {
                                Authorization: `Bearer ${sessionStorage.getItem('sessionTokenBearer')}`,
                                fileName: key,
                                token: sessionStorage.getItem('sessionToken'),
                                tipo: isCNH ? 'CNH' : 'OUTROS',
                                tipoDocumento: img0.documento
                            },
                        });

                        const data = await res.json();

                        if (data?.erros === false) {
                            const tipoServico = JSON.parse(sessionStorage.getItem('dadosLinkAtual')).outServico;
                            if (!isMounted) return;
                            const raw = sessionStorage.getItem('dadosLinkAtual');
                            const obj = raw ? JSON.parse(raw) : {};
                            obj.outArquivo = "S";
                            sessionStorage.setItem('dadosLinkAtual', JSON.stringify(obj));
                            navigate(tipoServico === 'KYC' ? '/comprovante-residencia' : (tipoServico === 'IDD' ? '/documentos-idd' : '/face'));
                        } else {
                            setMensagemErro({title: "Erro ao enviar arquivo para o ConformID",text: data.retorno ? data.retorno : String(data)});
                            setModalErroOpen(true);
                        }
                    } catch (err) {
                        setMensagemErro({title: "Erro ao tentar encaminhar arquivo para o ConformID",text: String(err)});
                        setModalErroOpen(true);
                    }
                } else {
                    setMensagemErro({title: "Erro ao enviar arquivo para o S3", text: String(putFile)});
                    setModalErroOpen(true);
                }
            } catch (error) {
                setMensagemErro({title: "Erro na rotina de geração de arquivo para upload", text: String(error)});
                setModalErroOpen(true);
            } finally {
                if (isMounted) setLoading(false);
            }
        })();

        return () => {
            isMounted = false;
        };

    }, [imagesUploaded, navigate, convertDoc]);

    const CaptureDocumentCaf = async (tipo) => {
        const captureResult = await documentDetector.current.capture({
            expectedDocument: "any",
            mode: tipo,
        });

        console.log('captureResult', captureResult);

        if (captureResult?.isCaptureValid === true && ["rg", "rg_new", "cnh", "cnh_new", "cnh_full", "cin", "cin_full", "rne", "rnm", "passport"].includes(captureResult.detectedDocument.type)) {
            const novoTipo = classifyBlobSimple(captureResult.image.blob);
            const novoLado = captureResult.detectedDocument.side;
            const novoDocumento = captureResult.detectedDocument.type;

            if (imagesUploaded.length === 0) {
                if((novoTipo === 'document' && novoLado === 'both') || novoTipo === 'image'){
                    const objtArquivo = {
                        blob: captureResult.image.blob,
                        documento: captureResult.detectedDocument.type,
                        tipo: novoTipo,
                        side: novoLado
                    };
                    setImagesUploaded([objtArquivo]);
                }
                else{
                    setMensagemErro({title: "Tipo de documento incorreto!", text: 'Leia as instruções, antes de tentar novamente.'});
                    setModalErroOpen(true);
                }
            } else {
                const anterior = imagesUploaded[imagesUploaded.length - 1];
                const ladosOpostos = (anterior.side === "front" && novoLado === "back") || (anterior.side === "back" && novoLado === "front");

                if (anterior.tipo === novoTipo && anterior.documento === novoDocumento && ladosOpostos) {
                    const objtArquivo = {
                        blob: captureResult.image.blob,
                        documento: captureResult.detectedDocument.type,
                        tipo: novoTipo,
                        side: novoLado
                    };
                    setImagesUploaded(prev => [...prev, objtArquivo]);
                }
                else {
                    setMensagemErro({title: "Tentativa inválida!", text: 'Verifique se é o mesmo documento da captura anterior, lado oposto ou tipo de arquivo semelhantes.'});
                    setModalErroOpen(true);
                }
            }
        }
        else{
            setMensagemErro({title: "Tentativa inválida!", text: 'Verifique as instruções e certifique-se que esta seguindo.'});
            setModalErroOpen(true);
        }
    }
    
    function docLabel(type) {
        const DOC_SHORT = {
            rg: 'RG',
            rg_new: 'Novo RG',
            cnh: 'CNH',
            cnh_new: 'Nova CNH',
            cin: 'CIN',
            rne: 'RNE',
            rnm: 'RNM',
            ctps: 'CTPS',
            passport: 'Passaporte',
        };

        if (!type) return 'Documento de identificação';
        const key = String(type).toLowerCase();
        return DOC_SHORT[key] || 'Documento de identificação';
    }

    const handleAskRemove = (index) => {
        setToDeleteIndex(index);
        setIsOpen(true);
    };

    const handleConfirmRemove = () => {
        if (toDeleteIndex == null) return;
        setImagesUploaded(prev => prev.filter((_, i) => i !== toDeleteIndex));
        setIsOpen(false);
        setToDeleteIndex(null);
    };

    return (
        <div className="viewer-page">
            <div className="viewer-content">
                <div className='content'>
                    {   
                        !loading 
                        
                        ?
                        
                        <div className="box-document gap-min">
                            <HeaderHora/>
                            <div className='status-face'>
                                <CountdownBar/>
                                <InternetStatus/>
                            </div>
                            <section className="doc-upload-instructions" aria-labelledby="doc-upload-title">
                                <header className="doc-header">
                                    <IdCard size={55} strokeWidth={1.5} color="var(--brand-color)"/>
                                    <div className='left-doc-header'>
                                        <h3 id="doc-upload-title">Identificação — Envio do documento com foto</h3>
                                        <p className="doc-subtitle">Vamos usar seu documento apenas para confirmar sua identidade.</p>
                                    </div>
                                </header>

                                <div className="doc-panels">
                                <div className="doc-panel">
                                    <p className="panel-text">
                                    Para iniciar o recadastramento, precisamos de um documento oficial com foto. Siga as orientações abaixo:
                                    </p>
                                </div>

                                <div className="doc-panel">
                                    <h4>Orientações para a foto do documento</h4>
                                    <ul className="checklist">
                                        <li>Use o documento original, sem capa ou plástico</li>
                                        <li>Tire uma foto do documento inteiro e bem legível (frente e verso, se houver)</li>
                                        <li>Tire a foto em local bem iluminado, sem sombras ou reflexos</li>
                                    </ul>
                                </div>

                                <div className="doc-panel">
                                    <h4>Privacidade</h4>
                                    <p className="panel-text">
                                    Suas imagens serão usadas apenas para confirmar sua identidade. Elas não serão utilizadas para outros fins.
                                    </p>
                                </div>
                                </div>
                            </section>

                            <div className='uploaded-documents-list'>
                                {imagesUploaded && imagesUploaded.map((blob, index) => (
                                <div key={index} className='uploaded-document-item'>
                                    <div className='left-uploaded-document'>
                                        <Image color='var(--brand-color)'/>
                                        <span>{blob.side === 'front' ? 'Frente do documento' : 'Verso do documento'}</span>
                                        <div className='pill-doc-type'>{docLabel(blob.documento)}</div>
                                    </div>
                                    <Trash onClick={() => handleAskRemove(index)} style={{ cursor: 'pointer' }} size={17} color='#F71A1A'/>
                                </div>
                                ))}
                            </div>

                            {(() => {
                                const hasFront = imagesUploaded?.some(it => it.side === 'front') || false;
                                const hasBack  = imagesUploaded?.some(it => it.side === 'back')  || false;

                                if ((hasFront || hasBack) && !(hasFront && hasBack)) {
                                    const missingText = !hasFront ? 'Falta a FRENTE do documento.' : 'Falta o VERSO do documento.';
                                    const docLabelName = imagesUploaded?.[0]?.documento ? docLabel(imagesUploaded[0].documento) : 'documento';

                                    return (
                                    <div className='missing-side-warning'>
                                        <strong>{missingText}</strong> <span>Por favor, capture ou anexe o lado faltante do "{docLabelName}" para continuar.</span>
                                    </div>
                                    );
                                }
                                return null;
                            })()}
                        </div>

                        :

                        <div className='content-loading'>
                            <span className="loader"></span>
                            <p className='text-load'>{loadingMsg.title}</p>
                            <p className='subtext-load'>{loadingMsg.subTitle}</p>
                        </div>
                    }

                    <ModalRemoveDocReact isOpen={isOpen} onRequestClose={() => setIsOpen(false)}>
                        <div className='text-4'>Deseja excluir este arquivo?</div>
                        <div className='text-5'>Ele será apagado permanentemente do sistema.</div>

                        <div className='buttons-group'>
                            <button onClick={() => setIsOpen(false)} className='button-secondary'>CANCELAR</button>
                            <button onClick={handleConfirmRemove}>CONFIRMAR</button>
                        </div>
                    </ModalRemoveDocReact>

                    <ModalErroReact isOpen={modalErroOpen} onRequestClose={() => setModalErroOpen(false)}>
                    <div className='icon-result'>
                        <CircleX color='#F71A1A' strokeWidth={2} size={120} />
                    </div>
                    <div className='text-4'>{mensagemErro.title}</div>
                    <div className='text-5'>{mensagemErro.text}</div>
                    <div className='buttons-group'>
                        <button onClick={() => setModalErroOpen(false)}>TENTAR NOVAMENTE</button>
                    </div>
                    </ModalErroReact>
                </div>
            </div>

            {
                loading ? null :
                <div className="viewer-footer"> 
                    <div onClick={() => CaptureDocumentCaf('automatic')} className='upload-document-button primary'>
                        <Camera />
                        <span>CAPTURAR DOCUMENTO</span>
                    </div>

                    {sessionStorage.getItem('ambienteOrigem') !== 'totemalprev' ? 
                        <div onClick={() => CaptureDocumentCaf('upload')} className='upload-document-button'>
                            <Upload />
                            <span>REALIZAR UPLOAD</span>
                        </div>
                    : null }
                </div>
            }
        </div>
    );
}

export function classifyBlobSimple(blob) {
  const t = blob.type || "";
  if (t.startsWith("image/")) return "image";
  if (
    t === "application/pdf" ||
    t === "application/msword" ||
    t === "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
  ) return "document";

  const name = blob?.name?.toLowerCase?.() ?? "";
  if (name.endsWith(".pdf") || name.endsWith(".doc") || name.endsWith(".docx")) return "document";
  if (name.match(/\.(jpg|jpeg|png|webp|heic)$/)) return "image";

  return "unknown";
}